import BackButton from '@/components/back-button/back-button';
import useEmailController from '@/components/decision-tree/components/SurveyItem/components/Question/questionTypes/Email/Email.controller';
import { EmailQuestion } from '@/components/decision-tree/components/SurveyItem/components/Question/questionTypes/Email/types';
import { AnswerModel } from '@/components/decision-tree/hooks';
import NavigationBar from '@/components/layout/navigation-bar/navigation-bar';
import customTheme from '@/constants/theme.custom';
import { TOAST_DEFAULT_OPTIONS } from '@/constants/toast';
import { useEnterKeyGlobal } from '@/hooks/use-enter-key';
import useIsMobile from '@/hooks/use-is-mobile';
import useOnMount from '@/hooks/useOnMount';
import useVisitorSession from '@/hooks/useVisitorSession';
import authService from '@/services/auth.service';
import { Box, Button, Image, InlineAlert, Input, Text, useToast } from '@cmpsr/components';
import { useCallback, useEffect, useRef, useState } from 'react';

type EmailCaptureProps = {
  onBackClick: () => void;
  showBackButton?: boolean;
  question: EmailQuestion;
  defaultValue?: any;
  saveAnswer: (payload: AnswerModel) => void;
  goToNextSurveyItem: () => void;
  isNextDisabled: boolean;
};

export const EmailCapture = ({
  onBackClick,
  showBackButton = true,
  question,
  defaultValue,
  saveAnswer,
  goToNextSurveyItem,
  isNextDisabled = false,
}: EmailCaptureProps) => {
  const isMobile = useIsMobile();
  const initialSaveDone = useRef(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const toast = useToast();
  const { captureLead } = useVisitorSession();
  const { onInputFn, form, validationOptions, onBlurFn } = useEmailController({
    validationValues: question?.validationValues,
    saveAnswer,
    defaultValue,
  });

  useOnMount(() => {
    // Focus the email input on mount
    setTimeout(() => {
      const emailInput = document.querySelector('input[name="email"]');
      if (emailInput) {
        (emailInput as HTMLInputElement).focus();
      }
    }, 0);
  });

  // If email is prefilled run the saveAnswer function to validate the form
  useEffect(() => {
    if (defaultValue?.value && !initialSaveDone.current && saveAnswer) {
      saveAnswer({ type: 'email', value: defaultValue.value });
      initialSaveDone.current = true;
    }
  }, [defaultValue, saveAnswer]);

  // Create lead and identify amplitude user
  const handleEmailCapture = useCallback(
    async (answer: AnswerModel) => {
      try {
        // Get email value safely for different answer types
        const emailValue = typeof answer === 'object' && 'value' in answer ? answer.value : '';

        // Get invite code from localStorage if present
        const inviteCode = authService.getInviteCode();

        // Capture Lead
        await captureLead(emailValue as string, inviteCode || undefined);
      } catch (err) {
        console.error('Failed to capture lead:', err);
        // Continue the flow even if lead creation fails
      }
    },
    [captureLead]
  );

  // Handle next button click: validate form, create lead, and go to next survey item
  const handleNextClick = async () => {
    setIsLoading(true);
    try {
      // Trigger form validation
      const isValid = await form.trigger('email');

      if (!isValid) {
        setIsLoading(false);
        return;
      }

      // Get the current email value from the form
      const emailValue = form.getValues('email');
      await handleEmailCapture({ type: 'email', value: emailValue });
      await goToNextSurveyItem();
    } catch (error) {
      toast({
        ...TOAST_DEFAULT_OPTIONS,
        title: 'Error',
        description: error,
        status: 'error',
      });
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  useEnterKeyGlobal(handleNextClick);

  return (
    <Box width="100%" height="100vh" background="tan" display="flex" flexDirection="column">
      <NavigationBar
        variant={isMobile ? 'light-minimal' : 'dark-minimal'}
        leftContent={showBackButton && <BackButton variant={isMobile ? 'dark' : 'light'} onClick={onBackClick} />}
      />

      <Box display="flex" flexDirection={{ base: 'column-reverse', lg: 'row' }} flex={1} overflow="hidden">
        <Box
          width={{ base: '100%', lg: '50%' }}
          height={{ base: '100%', lg: '100%' }}
          overflow="hidden"
          position="relative"
        >
          <Image
            srcSet="/images/ppra-email-capture/ppra-email-capture-400w.avif 400w, /images/ppra-email-capture/ppra-email-capture-600w.avif 600w, /images/ppra-email-capture/ppra-email-capture-800w.avif 800w, /images/ppra-email-capture/ppra-email-capture-1000w.avif 1000w, /images/ppra-email-capture/ppra-email-capture-1200w.avif 1200w"
            sizes="(max-width: 400px) 400px, (max-width: 600px) 600px, (max-width: 800px) 800px, (max-width: 1000px) 1000px, (min-width: 1001px) 1200px"
            src="/images/ppra-email-capture/ppra-email-capture.png"
            alt="Woman looking at cell phone while smiling"
            objectFit="cover"
            objectPosition={{ base: 'top', lg: 'center' }}
            width="100%"
            height={{ base: undefined, lg: '100%' }}
          />
        </Box>
        <Box
          width={{ base: '100%', lg: '50%' }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          py={{ base: 'spacer-16' }}
          px={{ base: 'spacer-8', lg: 'spacer-16' }}
        >
          <Box display="flex" flexDirection="column" gap="spacer-8" maxWidth="45rem" width="480px">
            <Text
              color={customTheme.colors['med-blue']}
              textTransform="uppercase"
              fontSize={{ base: '14px', lg: '16px' }}
              fontWeight="500"
            >
              {question?.description || 'Preliminary Results Ahead'}
            </Text>
            <Text
              fontFamily={customTheme.typography.fonts.inter}
              color="darkBlue"
              fontSize={{ base: '25px', lg: '35px' }}
              fontWeight="400"
              lineHeight="130%"
            >
              {'Enter your email for your'} {/* Kind of funky but we want results to be italicized */}
              <Text
                as="span"
                fontFamily="libreBaskerville"
                color="darkBlue"
                fontStyle="italic"
                fontSize={{ base: '30px', lg: '42px' }}
              >
                results
              </Text>
            </Text>

            <Box gap="spacer-0">
              <Input
                placeholder={(question as any)?.placeholder || 'Email Address'}
                color="black"
                backgroundColor={'transparent'}
                _hover={{
                  backgroundColor: 'transparent',
                }}
                _placeholder={{
                  color: 'black',
                }}
                p="20px"
                borderColor={form.formState.errors.email?.message ? 'alert-error-default !important' : 'black'}
                defaultValue={defaultValue?.value}
                type="email"
                autoComplete="email"
                {...form.register('email', validationOptions)}
                onBlur={onBlurFn}
                onInput={onInputFn}
                fontSize="16px"
              />
              {form.formState.errors.email?.message ? (
                <InlineAlert status="error" mt="spacer-6" mb="spacer-6" alignItems="flex-start">
                  <InlineAlert.Icon />
                  <InlineAlert.Message>
                    <Box color="salmon-light">{form.formState.errors.email?.message}</Box>
                  </InlineAlert.Message>
                </InlineAlert>
              ) : null}
            </Box>
            <Button
              variant="primary"
              backgroundColor={customTheme.colors.lightBlue}
              color="darkBlue"
              fontFamily={customTheme.typography.fonts.inter}
              type="submit"
              isLoading={isLoading}
              onClick={handleNextClick}
              width="fit-content"
              px={{ base: 'spacer-5', lg: 'spacer-8' }}
              py={{ base: 'spacer-5', lg: 'spacer-5' }}
            >
              Take the Assessment
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
